import { ReactNode, useEffect, useRef, useState } from 'react';

import PageHeader from 'components/ui/page-header';

// Spinner só ganha visibilidade se o carregamento durar mais que isto: nas remontagens
// quentes (caches de sessão) a guarda resolve em poucos frames e mostrar "Carregando…"
// por <100ms vira um pisca que parece defeito. Abaixo do limiar, o usuário vê apenas o
// shell estável da página.
const SPINNER_DELAY_MS = 250;

// Continuidade entre instâncias: no caminho FRIO o gate mostra o spinner e o fluxo
// remonta este componente — sem isto o timer zeraria e o spinner piscaria para fora e
// para dentro. Se um spinner esteve visível há menos que esta janela, a instância nova
// já nasce com ele.
const SPINNER_CONTINUITY_MS = 1000;

let lastSpinnerVisibleAt = 0;

// Shell comum das telas de transição da apreciação — usado pelo loading e pelos estados de erro/sem-acesso do
// gate, para manter a página "montada" enquanto os dados resolvem. É o mesmo topo e o mesmo contêiner da fila
// (`TopoDoGestor`): antes era o cabeçalho antigo "Minha Equipe", que piscava antes da tela nova (MEQ2-10).
export function FlowShell({ children, ocupada = false }: { children: ReactNode; ocupada?: boolean }) {
  return (
    <div className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0" aria-busy={ocupada || undefined}>
      <PageHeader titulo="Realização Destaque" voltar={{ to: '/', destino: 'a página inicial' }} />
      {children}
    </div>
  );
}

// Tela de carregamento dos fluxos de apreciação (ELE-02): shell da página + spinner
// acessível. Substitui o `return null` das guardas de render — que deixava a tela em
// branco (impressão de erro) enquanto user/ciclo/fase resolvem. Renderizada também
// pelo gate, a transição gate → fluxo vira um loading contínuo, sem "pisca".
export function FlowLoading() {
  const [showSpinner, setShowSpinner] = useState(() => Date.now() - lastSpinnerVisibleAt < SPINNER_CONTINUITY_MS);
  const visibleRef = useRef(showSpinner);
  visibleRef.current = showSpinner;

  useEffect(() => {
    const timer = window.setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS);

    return () => {
      window.clearTimeout(timer);

      if (visibleRef.current) {
        lastSpinnerVisibleAt = Date.now();
      }
    };
  }, []);

  return (
    <FlowShell ocupada>
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4" role="status" aria-live="polite">
        {showSpinner && (
          <>
            <div
              className="h-10 w-10 animate-spin rounded-full border-4 border-brand-blue border-t-transparent motion-reduce:animate-none"
              aria-hidden="true"
            />
            <p className="m-0 text-sm text-content">Carregando realizações...</p>
          </>
        )}
      </div>
    </FlowShell>
  );
}
